import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { CardComponent } from 'src/app/shared/components/master-detail-layout/card/card.component';
import { MasterDetailLayoutComponent } from 'src/app/shared/components/master-detail-layout/master-detail-layout.component';
import { SidebarItemComponent } from 'src/app/shared/components/master-detail-layout/sidebar-item/sidebar-item.component';
import { FidelityPrizeFormService } from '../../services/fidelity-prize-form.service';
import { FidelityDataCatalogService } from '../../services/fidelity-data-catalog.service';
import { FidelityNavigationStateService } from '../../services/fidelity-navigation-state.service';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { LoaderService } from 'src/app/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { restManager } from 'app/ajs-upgraded-providers';
import { ChainCampaigns, ChainPrizes, Items } from 'tilby-models';
import { PrizesFields, PrizesFormGroups } from '../../models/fidelity-prize.model';
import { areObjectsEqual } from '../../utils/fidelity.utils';
import { ListSelectorDialogService } from 'src/app/dialogs/list-selector-dialog/list-selector-dialog.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TilbyDatetimePickerComponent } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Subscription } from 'rxjs';
import { mobileCheck } from 'src/utilities';

@Component({
  selector: 'app-fidelity-prizes',
  standalone: true,
  imports: [
    CommonModule,
    MasterDetailLayoutComponent,
    CardComponent,
    SidebarItemComponent,
    ActionBtnComponent,
    MatIconModule, MatButtonModule,
    MatFormFieldModule, ReactiveFormsModule, FormsModule,
    MatInputModule, MatSelectModule,
    TranslateModule, TruncatedTextDirective,
    MatDatepickerModule, TilbyDatetimePickerComponent
  ],
  templateUrl: './fidelity-prizes.component.html',
  styleUrl: './fidelity-prizes.component.scss'
})
export class FidelityPrizesComponent implements OnInit, OnDestroy {

  readonly prizeFormService = inject(FidelityPrizeFormService);
  private readonly fidelityDataCatalogService = inject(FidelityDataCatalogService);
  private readonly translateService = inject(TranslateService);
  private readonly fidelityNavigationStateService = inject(FidelityNavigationStateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly loaderService = inject(LoaderService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly restManagerService = inject(restManager);
  private readonly listSelectorDialogService = inject(ListSelectorDialogService);

  protected prizesList: PrizesFields[] = [];
  protected itemsList: Items[] = [];
  protected itemsMap: Record<string, Items>;  
  protected campaignsList: ChainCampaigns[] = [];
  
  protected selectedPrize: PrizesFields | null = null;

  protected initialData!: PrizesFields;
  protected prizeForm!: PrizesFormGroups;
  protected prizeFormReady = this.prizeFormService.prizeFormReady;

  protected prizeTypes = [
    { value: 'discount_fix', name: this.translateService.instant('FIDELITY.SETUP.PRIZES.DISCOUNT_FIX') },
    { value: 'discount_perc', name: this.translateService.instant('FIDELITY.SETUP.PRIZES.DISCOUNT_PERC') },
    { value: 'gift', name: this.translateService.instant('FIDELITY.SETUP.PRIZES.GIFT') }
  ]

  protected isEdit = false;
  private typeSubscription?: Subscription;

  protected isMobile = mobileCheck();
  protected showDetailMobile = false;

  constructor() {
    this.prizesList = this.fidelityDataCatalogService.getDataByKey('prizes') as PrizesFields[];
    this.itemsList = this.fidelityDataCatalogService.getDataByKey('items') as Items[];
    this.itemsMap = this.fidelityDataCatalogService.getDataByKey('itemsMap') as Record<string, Items>;
    this.campaignsList = this.fidelityDataCatalogService.getDataByKey('campaigns') as ChainCampaigns[];
  }

  listenToTypeChanges() {
    this.typeSubscription?.unsubscribe();

    this.typeSubscription = this.prizeFormService.type?.valueChanges.subscribe(type => {
      this.setAsyncValidators(type);
    });
  }

  setAsyncValidators(type: string) {
    const itemControl = this.prizeFormService.item_sku;
    const discountControl = this.prizeFormService.discount_amount;

    if (!itemControl || !discountControl) return;

    if (type === 'gift') {
      itemControl?.setValidators([Validators.required]);
      discountControl?.clearValidators();
    } else {
      const validators = type === 'discount_perc'? [Validators.required, Validators.min(0), Validators.max(100)] : 
        [Validators.min(0), Validators.required];
      discountControl?.setValidators(validators);
      itemControl?.clearValidators();
    }
    itemControl?.updateValueAndValidity();
    discountControl?.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.fidelityNavigationStateService.register(() => this.checkChanges());
  }

  checkChanges(): boolean {
    if (!this.prizeForm) return false;
    const equal = areObjectsEqual(this.initialData, this.prizeForm.getRawValue());
    return !equal;
  }
  
  isSelected(prize: ChainPrizes) {
    if (this.selectedPrize) {
      return prize.id === this.selectedPrize.id;
    } 
    return false;
  }

  get isNotEmptyState() {
    return this.prizeFormReady() && (this.selectedPrize || !this.isEdit)
  }

  async selectPrize($event: any, index: number) {
    $event.stopPropagation();

    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.initPrize(index);
    if (this.isMobile) {
      this.showDetailMobile = true;
    }
  }

  async goBack() {
    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.prizeForm = this.prizeFormService.createForm(this.selectedPrize);
    this.showDetailMobile = false;
  }

  async checkPendingData() {
    if (this.fidelityNavigationStateService.hasUnsavedChanges()) {
      const confirmed = await this.confirmDialogService.openDialog({
        data: {
          messageLabel: 'FIDELITY.SETUP.PRIZES.EDIT_CANCEL_CONFIRM',
          confirmLabel: 'APPLICATION.YES',
          cancelLabel: 'APPLICATION.NO'
        }
      });

      if(!confirmed) return false;
    }
    return true;
  }

  editItem() {
    const skuToFind = this.prizeFormService.item_sku?.value;
    const dataSet = [...this.itemsList];
    const linkedItem = skuToFind ? this.itemsMap[skuToFind] : undefined;

    this.listSelectorDialogService.openDialog({
        data: {
          title: "DIALOG.LIST_SELECTOR.TITLE.ITEM",
          optionList: dataSet, 
          optionValue: linkedItem
        }
      }
    ).then((res: any) => {
      if(res) {
        this.prizeFormService.item_sku?.setValue(res.sku);
        this.prizeFormService.item_name?.setValue(res.name);
      }
    })
  }  

  initPrize(index: number) {
    if(index < 0) return;

    // out of range (e.g. the last prize was deleted): nothing is selected and the empty state is shown
    this.selectedPrize = this.prizesList[index] ?? null;

    this.prizeForm = this.prizeFormService.createForm(this.selectedPrize);
    this.initialData = structuredClone(this.prizeForm.getRawValue()) as PrizesFields;
    this.isEdit = true;

    this.setAsyncValidators(this.prizeFormService.type?.value);
    this.listenToTypeChanges();
  }

  async addPrize() {
    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.selectedPrize = null;
    this.prizeForm = this.prizeFormService.createForm(this.selectedPrize);
    this.initialData = structuredClone(this.prizeForm.getRawValue()) as PrizesFields;
    this.isEdit = false;

    this.setAsyncValidators(this.prizeFormService.type?.value);
    this.listenToTypeChanges();

    if (this.isMobile) {
      this.showDetailMobile = true;
    }
  }

  async deletePrize() {
    const idPrize = this.selectedPrize?.id;
    if(!idPrize) return;

    const confirmed = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'FIDELITY.SETUP.PRIZES.DELETE_CONFIRM',
        confirmLabel: 'APPLICATION.YES',
        cancelLabel: 'APPLICATION.NO'
      }
    });

    if(!confirmed) return;

    this.loaderService.show();

    try {
      await this.restManagerService.deleteOne("prizes", idPrize);

      this._snackBar.open(this.translateService.instant('FIDELITY.SETUP.PRIZES.DELETE_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      await this.reloadPrizes();

      if (this.isMobile) {
        this.showDetailMobile = false;
      }
    } catch(error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.PRIZES.DELETE_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async savePrize() {
    if (this.prizeForm.invalid) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.PRIZES.SAVE_INCORRECT_DATA')
      }});
      return;
    }

    const prizeToSend = this.prizeFormService.prepareDataForSave();

    this.loaderService.show();
    const isEdit = !!prizeToSend?.id;

    try {
      let result: ChainPrizes;

      if (isEdit) {
        result = await this.restManagerService.put("prizes", prizeToSend.id, prizeToSend);
      } else {
        result = await this.restManagerService.post("prizes", prizeToSend);
      }

      this._snackBar.open(this.translateService.instant(isEdit ? 'FIDELITY.SETUP.PRIZES.SAVE_EDIT_OK' : 'FIDELITY.SETUP.PRIZES.SAVE_CREATE_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      await this.reloadPrizes(result.id);

      if (this.isMobile) {
        this.showDetailMobile = false;
      }
    } catch (error: any) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.PRIZES.SAVE_ERROR')
      }});
    } finally {
      this.loaderService.hide();
    }
  }

  async updatePrizes(id?: number) {
    const prizes = await this.restManagerService.getList("prizes", { pagination: false });
    this.fidelityDataCatalogService.setProcessedPrizes(prizes);
    const updatedList = this.fidelityDataCatalogService.getDataByKey("prizes");
    this.prizesList = updatedList as PrizesFields[];

    const newIndex = id? this.prizesList.findIndex(c => c.id === id) : 0;
    this.initPrize(Math.max(newIndex, 0));
  }

  /** Reloads the list after a change: a failure must not be reported as a failure of the change itself, which succeeded */
  private async reloadPrizes(id?: number) {
    try {
      await this.updatePrizes(id);
    } catch (error) {
      console.error('Failed to reload the prizes', error);
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.LOAD_ERROR')
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.fidelityNavigationStateService.unregister();
    this.typeSubscription?.unsubscribe(); 
    this.prizeFormReady.set(false);
  }
}